export type Verdict = 'valid' | 'invalid' | 'unknown';

export type ReasonCode =
  | 'SHORT_URL_AUTHORIZED'
  | 'SHORT_URL_NOT_FOUND'
  | 'CERT_FOUND'
  | 'CERT_NOT_FOUND'
  | 'CERT_REVOKED'
  | 'CERT_EXPIRED'
  | 'URL_PARSE_FAILED';

export type CertificateData = {
  id?: number;
  name: string;
  competency: string;
  issued: string;
  expiry: string;
  status: string;
  contract: string;
  tokenId: number;
  networkId: number;
  imageURI?: string;
  explorerUrl?: string;
  shortUrl?: string;
  tenant?: {
    id: number;
    slug: string;
    name: string;
    issuerName?: string | null;
    logoUrl?: string | null;
  } | null;
};

export type VerificationResult = {
  verdict: Verdict;
  reasons: ReasonCode[];
  certificate?: CertificateData;
  shortUrlStatus?: 'authorized' | 'not_found' | 'skipped';
  originalUrl?: string;
};

const NEGATIVE_REASONS: ReasonCode[] = [
  'SHORT_URL_NOT_FOUND',
  'CERT_NOT_FOUND',
  'CERT_REVOKED',
  'CERT_EXPIRED',
  'URL_PARSE_FAILED',
];

export function computeVerdict(reasons: ReasonCode[]): Verdict {
  if (reasons.some((r) => NEGATIVE_REASONS.includes(r))) return 'invalid';
  if (reasons.includes('CERT_FOUND')) return 'valid';
  return 'unknown';
}

export function getVerdictColor(verdict: Verdict): string {
  switch (verdict) {
    case 'valid':
      return 'bg-green-600 text-white';
    case 'invalid':
      return 'bg-red-600 text-white';
    case 'unknown':
      return 'bg-gray-500 text-white';
  }
}

export function getVerdictLabel(verdict: Verdict): string {
  switch (verdict) {
    case 'valid':
      return 'VALID';
    case 'invalid':
      return 'INVALID';
    case 'unknown':
      return 'UNKNOWN';
  }
}

export function getVerdictNote(verdict: Verdict, reasons: ReasonCode[]): string {
  if (reasons.includes('SHORT_URL_NOT_FOUND')) return 'Short URL could not be resolved.';
  if (reasons.includes('URL_PARSE_FAILED')) return 'The URL could not be recognised as a certificate link.';
  if (reasons.includes('CERT_NOT_FOUND')) return 'No certificate found for this address.';
  if (reasons.includes('CERT_REVOKED')) return 'Certificate has been revoked by the issuer.';
  if (reasons.includes('CERT_EXPIRED')) return 'Certificate has passed its expiry date.';
  if (verdict === 'valid') return 'Certificate is valid and active.';
  return 'Unable to determine certificate status.';
}

export function getReasonLabel(reason: ReasonCode): string {
  switch (reason) {
    case 'SHORT_URL_AUTHORIZED':
      return 'Short URL resolved';
    case 'SHORT_URL_NOT_FOUND':
      return 'Short URL not found';
    case 'CERT_FOUND':
      return 'Certificate found';
    case 'CERT_NOT_FOUND':
      return 'Certificate not found';
    case 'CERT_REVOKED':
      return 'Certificate revoked';
    case 'CERT_EXPIRED':
      return 'Certificate expired';
    case 'URL_PARSE_FAILED':
      return 'URL not recognised';
  }
}

export function getReasonType(reason: ReasonCode): 'positive' | 'negative' {
  return NEGATIVE_REASONS.includes(reason) ? 'negative' : 'positive';
}
